import { adiApi } from "./api";
import {
  CreatePrincipalRequest,
  CreatePrincipalResponse,
  GetPrincipalRequest,
  GetPrincipalResponse,
  ListPrincipalsRequest,
  ListPrincipalsResponse,
  DeletePrincipalRequest,
  DeletePrincipalResponse,
  ListTablesRequest,
  ListTablesResponse,
  ExecuteQueryRequest,
  ExecuteQueryResponse,
  GetQueryResultsRequest,
  GetQueryResultsResponse,
  ListQueriesRequest,
  ListQueriesResponse,
  CreateExportRequest,
  CreateExportResponse,
  GetExportRequest,
  GetExportResponse,
  ListExportFilesRequest,
  ListExportFilesResponse,
  ListExportsRequest,
  ListExportsResponse,
  CreateReportRequest,
  CreateReportResponse,
  GetReportRequest,
  GetReportResponse,
  ListReportsRequest,
  ListReportsResponse,
  DeleteReportRequest,
  DeleteReportResponse,
  UpdatePrincipalDatasetsRequest,
  UpdatePrincipalDatasetsResponse,
  UpdatePrincipalRequest,
  UpdatePrincipalResponse,
  GetDownloadUrlRequest,
  GetDownloadUrlResponse,
  GetTeamConfigRequest,
  GetTeamConfigResponse,
  CreatePrincipalDatasetRequest,
  CreatePrincipalDatasetResponse,
  GetPrincipalDatasetRequest,
  GetPrincipalDatasetResponse,
  CreateTemplateRequest,
  CreateTemplateResponse,
  GetTemplateRequest,
  GetTemplateResponse,
} from "./types";

const extendedApi = adiApi.injectEndpoints({
  endpoints: (builder) => ({
    // Principal endpoints
    createPrincipal: builder.mutation<
      CreatePrincipalResponse,
      CreatePrincipalRequest
    >({
      query: (body) => ({
        url: "/createPrincipal",
        method: "POST",
        body,
      }),
      // Shares the /createPrincipal resource with createPrincipalDataset;
      // invalidate both tags to keep caches consistent across both paths.
      invalidatesTags: ["PrincipalDatasets", "Principals"],
    }),

    getPrincipal: builder.query<GetPrincipalResponse, GetPrincipalRequest>({
      query: ({ teamId }) => ({
        url: `/getPrincipal/${encodeURIComponent(teamId)}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { teamId }) => [
        { type: "PrincipalDatasets", id: teamId },
        { type: "Principals", id: teamId },
      ],
    }),

    updatePrincipalDatasets: builder.mutation<
      UpdatePrincipalDatasetsResponse,
      UpdatePrincipalDatasetsRequest
    >({
      query: (body) => ({
        url: "/updatePrincipalDatasets",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { teamId }) => [
        { type: "PrincipalDatasets", id: teamId },
      ],
    }),

    // Table endpoints
    listTables: builder.query<ListTablesResponse, ListTablesRequest>({
      query: ({ principalId, nextToken, maxResults }) => {
        const params = new URLSearchParams();
        if (nextToken) {
          params.set("nextToken", nextToken);
        }
        if (maxResults) {
          params.set("maxResults", String(maxResults));
        }
        const queryString = params.toString();
        return `/tables/${encodeURIComponent(principalId)}${queryString ? `?${queryString}` : ""}`;
      },
    }),

    // Query endpoints
    executeQuery: builder.mutation<ExecuteQueryResponse, ExecuteQueryRequest>({
      query: (body) => ({
        url: "/query/execute",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Queries"],
    }),

    getQueryResults: builder.query<
      GetQueryResultsResponse,
      GetQueryResultsRequest
    >({
      query: ({ queryId, nextToken, maxResults }) => {
        const params = new URLSearchParams();
        if (nextToken) {
          params.set("nextToken", nextToken);
        }
        if (maxResults) {
          params.set("maxResults", String(maxResults));
        }
        const queryString = params.toString();
        return {
          url: `/query/${encodeURIComponent(queryId)}${queryString ? `?${queryString}` : ""}`,
          method: "GET",
        };
      },
      providesTags: (_result, _error, { queryId }) => [
        { type: "Queries", id: queryId },
      ],
    }),

    listQueries: builder.query<ListQueriesResponse, ListQueriesRequest>({
      query: ({ principalId, nextToken, maxResults, status }) => {
        const params = new URLSearchParams();
        if (nextToken) {
          params.set("nextToken", nextToken);
        }
        if (maxResults) {
          params.set("maxResults", String(maxResults));
        }
        if (status) {
          params.set("status", status);
        }
        const queryString = params.toString();
        return `/query/list/${encodeURIComponent(principalId)}${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: ["Queries"],
    }),

    // Export endpoints
    createExport: builder.mutation<CreateExportResponse, CreateExportRequest>({
      query: (body) => ({
        url: "/createExport",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Exports"],
    }),

    getExport: builder.query<GetExportResponse, GetExportRequest>({
      query: ({ exportId }) => ({
        url: `/getExport/${encodeURIComponent(exportId)}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { exportId }) => [
        { type: "Exports", id: exportId },
      ],
    }),

    listExports: builder.query<ListExportsResponse, ListExportsRequest>({
      query: ({ principalId, nextToken }) => {
        const params = new URLSearchParams();
        if (nextToken) {
          params.set("nextToken", nextToken);
        }
        const queryString = params.toString();
        return `/listExports/${encodeURIComponent(principalId)}${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: ["Exports"],
    }),

    listExportFiles: builder.query<
      ListExportFilesResponse,
      ListExportFilesRequest
    >({
      query: ({ exportId, maxResults, nextToken }) => {
        const params = new URLSearchParams();
        if (maxResults) {
          params.set("maxResults", String(maxResults));
        }
        if (nextToken) {
          params.set("nextToken", nextToken);
        }
        const queryString = params.toString();
        return `/listExportFiles/${encodeURIComponent(exportId)}${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: (_result, _error, { exportId }) => [
        { type: "Exports", id: exportId },
      ],
    }),

    getDownloadUrl: builder.mutation<
      GetDownloadUrlResponse,
      GetDownloadUrlRequest
    >({
      query: (body) => ({
        url: "/getDownloadUrl",
        method: "POST",
        body,
      }),
    }),

    updatePrincipal: builder.mutation<
      UpdatePrincipalResponse,
      UpdatePrincipalRequest
    >({
      query: (body) => ({
        url: "/updatePrincipal",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Principals"],
    }),

    // Report endpoints
    createReport: builder.mutation<CreateReportResponse, CreateReportRequest>({
      query: (body) => ({
        url: "/createReport",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Reports"],
    }),

    getReport: builder.query<GetReportResponse, GetReportRequest>({
      query: ({ reportId }) => ({
        url: `/getReport/${encodeURIComponent(reportId)}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { reportId }) => [
        { type: "Reports", id: reportId },
      ],
    }),

    listReports: builder.query<ListReportsResponse, ListReportsRequest>({
      query: ({ principalId, nextToken }) => {
        const params = new URLSearchParams();
        if (nextToken) {
          params.set("nextToken", nextToken);
        }
        const queryString = params.toString();
        return `/listReports/${encodeURIComponent(principalId)}${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: ["Reports"],
    }),

    deleteReport: builder.mutation<DeleteReportResponse, DeleteReportRequest>({
      query: ({ reportId }) => ({
        url: "/deleteReport",
        method: "POST",
        body: { reportId },
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        { type: "Reports", id: reportId },
        "Reports",
      ],
    }),

    // Principals list/delete endpoints (Data & Insights)
    listPrincipals: builder.query<
      ListPrincipalsResponse,
      ListPrincipalsRequest
    >({
      query: ({ teamId, nextToken, maxResults }) => {
        const params = new URLSearchParams();
        params.set("teamId", teamId);
        if (nextToken) {
          params.set("nextToken", nextToken);
        }
        if (maxResults) {
          params.set("pageSize", String(maxResults));
        }
        const queryString = params.toString();
        return {
          url: `/listPrincipals?${queryString}`,
          method: "GET",
        };
      },
      providesTags: ["Principals"],
    }),

    deletePrincipal: builder.mutation<
      DeletePrincipalResponse,
      DeletePrincipalRequest
    >({
      query: ({ principalId }) => ({
        url: "/deletePrincipal",
        method: "POST",
        body: { principalId },
      }),
      invalidatesTags: ["Principals"],
    }),

    // Team Config endpoint (TAR Data Extraction)
    createTemplate: builder.mutation<
      CreateTemplateResponse,
      CreateTemplateRequest
    >({
      query: (body) => ({
        url: "/createTemplate",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Templates"],
    }),

    getTemplate: builder.query<GetTemplateResponse, GetTemplateRequest>({
      query: ({ templateId }) => ({
        url: `/getTemplate/${encodeURIComponent(templateId)}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { templateId }) => [
        { type: "Templates", id: templateId },
      ],
    }),

    getTeamConfig: builder.query<GetTeamConfigResponse, GetTeamConfigRequest>({
      query: ({ teamId }) => `/getTeamConfig/${encodeURIComponent(teamId)}`,
    }),

    // Create Principal Dataset (TAR Data Extraction)
    createPrincipalDataset: builder.mutation<
      CreatePrincipalDatasetResponse,
      CreatePrincipalDatasetRequest
    >({
      query: (body) => ({
        url: "/createPrincipal",
        method: "POST",
        body,
      }),
      // Same backend resource as createPrincipal (/createPrincipal); invalidate
      // both tags so caches under either type stay consistent.
      invalidatesTags: ["Principals", "PrincipalDatasets"],
    }),

    // Get Principal Dataset (Dataset Detail)
    getPrincipalDataset: builder.query<
      GetPrincipalDatasetResponse,
      GetPrincipalDatasetRequest
    >({
      query: ({ principalId }) => ({
        url: `/getPrincipal/${encodeURIComponent(principalId)}`,
        method: "GET",
      }),
      // Same backend resource as getPrincipal (/getPrincipal/{id}); provide both
      // tags so a write through either path refreshes this read.
      providesTags: (_result, _error, { principalId }) => [
        { type: "Principals", id: principalId },
        { type: "PrincipalDatasets", id: principalId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreatePrincipalMutation,
  useGetPrincipalQuery,
  useLazyGetPrincipalQuery,
  useUpdatePrincipalDatasetsMutation,
  useListPrincipalsQuery,
  useDeletePrincipalMutation,
  useListTablesQuery,
  useLazyListTablesQuery,
  useExecuteQueryMutation,
  useGetQueryResultsQuery,
  useLazyGetQueryResultsQuery,
  useListQueriesQuery,
  useLazyListQueriesQuery,
  useCreateExportMutation,
  useGetExportQuery,
  useLazyGetExportQuery,
  useListExportsQuery,
  useLazyListExportsQuery,
  useListExportFilesQuery,
  useLazyListExportFilesQuery,
  useGetDownloadUrlMutation,
  useUpdatePrincipalMutation,
  useCreateReportMutation,
  useGetReportQuery,
  useLazyGetReportQuery,
  useListReportsQuery,
  useLazyListReportsQuery,
  useDeleteReportMutation,
  useGetTeamConfigQuery,
  useCreatePrincipalDatasetMutation,
  useGetPrincipalDatasetQuery,
  useCreateTemplateMutation,
  useGetTemplateQuery,
  useLazyGetTemplateQuery,
} = extendedApi;
